/**
 * /platform/community — SEO landing page for the Community Forum.
 */
import React from 'react';
import {
  Users, MessageSquare, Bell, Award, ShieldCheck, TrendingUp,
  Sparkles, BookOpen, Bot, Video, Search, ThumbsUp, HeartHandshake,
  Rocket, ChefHat, Building2,
} from 'lucide-react';
import PlatformFeaturePage from './PlatformFeaturePage';

const CommunityPage = () => (
  <PlatformFeaturePage
    slug="community"
    eyebrow="Community Forum"
    title="Talk shop with operators"
    titleAccent="who run the same gear."
    subtitle="A moderated, threaded forum for hobbyists, side-hustlers and full-scale operators — no Facebook algorithm, no crypto spam, just real freeze-drying conversation."
    seo={{
      title: 'Freeze Dry Community Forum — Real Operators, Real Answers',
      description: 'Threaded discussions on cycles, machines, recipes and business. Get notified when someone replies to you. Moderated by the academy team.',
      keywords: 'freeze dry forum, freeze drying community, freeze dryer forum, freeze dry business discussion',
    }}
    highlights={[
      { icon: MessageSquare, label: 'Threaded discussions' },
      { icon: Bell,          label: 'Reply notifications' },
      { icon: Award,         label: 'Verified operators' },
      { icon: ShieldCheck,   label: 'Actively moderated' },
    ]}
    primaryCta={{ label: 'Join the community', to: '/login?mode=signup' }}
    secondaryCta={{ label: 'See the directory of pros', to: '/directory' }}
    howItWorks={[
      { title: 'Pick a category',   body: 'Machines, recipes, business, troubleshooting, marketplace — categorized so replies stay on topic and searchable.' },
      { title: 'Start a thread',    body: 'Post a question or share a batch. Rich text, image attachments, and quote-replies make threads readable weeks later.' },
      { title: 'Get pinged on replies', body: 'Every reply lands in your notifications inbox (and on your phone via web-push if you enable it). No more "did anyone answer my post?"' },
    ]}
    features={[
      { icon: MessageSquare, title: 'Real threading',       body: 'Nested replies keep sub-conversations tidy — not one flat mess of 40 comments arguing about vacuum pumps.' },
      { icon: Bell,          title: 'Reply notifications',  body: 'You get pinged the second someone answers your thread — in the app bell and (opt-in) as a push on your phone.' },
      { icon: Search,        title: 'Actually searchable',  body: 'Full-text search across every thread ever — because "was there a thread about my strawberry issue last year?" should have an answer.' },
      { icon: Award,         title: 'Verified profiles',    body: 'Operators can verify machines they run + years of experience. Advice from a 6-year medium-freeze-dryer operator carries the weight it should.' },
      { icon: ShieldCheck,   title: 'Moderated',            body: 'A small academy team removes spam, gates crypto/get-rich schemes, and mediates disputes. The result: signal, not noise.' },
      { icon: ThumbsUp,      title: 'Upvotes + best answers', body: 'Great replies rise to the top. Thread starters can mark a "best answer" so future readers get the gold straight away.' },
    ]}
    personas={[
      { icon: Rocket,     title: 'First-batch fear',        body: 'Nervous about your first cycle? Post it. The community will walk you through it live in the replies.' },
      { icon: ChefHat,    title: 'Recipe experimenters',    body: 'Share your best cycle for a new food. Get feedback on what worked and what didn\'t — building a shared cookbook over time.' },
      { icon: Building2,  title: 'Business operators',      body: 'Compare notes on labor cost, packaging suppliers, licensing hurdles — real numbers from real operators, not Instagram influencers.' },
    ]}
    faqs={[
      { q: 'Is the forum free?',
        a: 'Reading is free forever. Posting requires a free account (10 seconds to make). Pro members get a badge on their posts.' },
      { q: 'How is it different from Facebook groups?',
        a: 'Real threading, real search, real notifications, real moderation. No algorithm hiding your post because you don\'t "engage enough". No ads for MLMs. And your history isn\'t owned by a social network.' },
      { q: 'Are the moderators paid?',
        a: 'Some are academy staff, others are trusted community members. All are experienced operators — nobody\'s a bot moderator.' },
      { q: 'Can I export my own posts?',
        a: 'Yes — full data export from your profile. Your history is yours.' },
    ]}
    related={[
      { slug: 'directory',   title: 'Vendor Directory',   icon: Building2, blurb: 'When you need a pro, not a peer.' },
      { slug: 'ai-assistant',title: 'AI Assistant',       icon: Bot,       blurb: 'For questions with an objective answer.' },
      { slug: 'learning',    title: 'Learning & Courses', icon: BookOpen,  blurb: 'When you want the structured version.' },
    ]}
    finalCtaTitle="Freeze-drying gets lonely without a community."
    finalCtaBody="Sign up free and post your first question — you\u2019ll usually get a reply within an hour."
  />
);

export default CommunityPage;
